import apiClient from '../axios.js'

/**
 * 訂閱客戶管理 API
 */
export const subscriptionClientsService = {
    /**
     * 獲取訂閱客戶列表（系統管理）
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {string} params.search - 搜尋關鍵字
     * @param {string} params.platformId - 平台 ID（可選）
     * @returns {Promise<Object>} 訂閱客戶列表
     */
    getSubscriptionClients: async (params = {}) => {
        const response = await apiClient.get('/system/subscription-clients', { params })
        return response
    },

    /**
     * 獲取單一客戶的詳細訂閱與扣款資訊（系統管理）
     * @param {string} clientId - 客戶 ID
     * @returns {Promise<Object>} 客戶詳細資訊
     */
    getClientSubscriptionDetails: async (clientId) => {
        const response = await apiClient.get(`/system/subscription-clients/${clientId}`)
        return response
    },

    /**
     * 延長訂閱（人工收款）
     * @param {string} clientId - 客戶 ID
     * @param {Object} data - 延長訂閱資料
     * @param {string} data.paymentDate - 收款日期（必填，格式：YYYY-MM-DD）
     * @param {number} data.amount - 收款金額（可選，未提供則自動計算）
     * @param {string} data.notes - 備註（可選）
     * @returns {Promise<Object>} 延長訂閱結果
     */
    extendSubscription: async (clientId, data) => {
        const response = await apiClient.post(`/client-subscriptions/${clientId}/extend`, data)
        return response
    },

    /**
     * 取消訂閱自動續約（客戶可使用至目前週期結束）
     */
    cancelSubscriptionRenewal: async (clientId) => {
        const response = await apiClient.post(`/client-subscriptions/${clientId}/cancel-renewal`, {})
        return response
    },

    /**
     * 建立新訂閱（用於沒有活躍訂閱的客戶）
     * @param {string} clientId - 客戶 ID
     * @param {Object} data - 訂閱資料
     * @param {string} data.planId - 方案 ID（必填）
     * @param {string} data.paymentType - 付款方式（必填，'manual' 或 'ecpay'）
     * @param {string} data.billingCycle - 計費週期（可選，'monthly' 或 'yearly'，預設 'monthly'）
     * @param {string} data.startDate - 開始日期（可選，格式：YYYY-MM-DD，預設今天）
     * @param {number} data.durationDays - 訂閱天數（可選，預設 30 天）
     * @returns {Promise<Object>} 建立訂閱結果
     */
    createSubscription: async (clientId, data) => {
        const response = await apiClient.post(`/client-subscriptions/${clientId}/create`, data)
        return response
    },

    /**
     * 升級為正式訂閱（試用中直接轉正，取消現有試用並建立新付費訂閱）
     * @param {string} clientId - 客戶 ID
     * @param {Object} data - 升級資料
     * @param {string} data.planId - 方案 ID（必填）
     * @param {string} data.paymentType - 付款方式（必填，'manual' 或 'ecpay'）
     * @param {string} data.billingCycle - 計費週期（可選，'monthly' 或 'yearly'，預設 'monthly'）
     * @returns {Promise<Object>} 升級結果
     */
    upgradeSubscription: async (clientId, data) => {
        const response = await apiClient.post(`/client-subscriptions/${clientId}/upgrade`, data)
        return response
    },

    /**
     * 確認待收款升級訂閱（試用升級選「人工收款」後，客戶已付款時由管理員確認）
     */
    confirmPendingSubscription: async (clientId, data) => {
        const response = await apiClient.post(
            `/client-subscriptions/${clientId}/confirm-pending-subscription`,
            data
        )
        return response
    },

    /**
     * 訂閱扣款全額退款（系統／平台後台）
     * @param {string} clientId
     * @param {string} paymentRecordId
     * @param {Object} [data]
     * @param {string} [data.operatorNote]
     * @param {boolean} [data.refundWithoutEcpay] - 略過綠界 API（已在後台完成退款時）
     */
    refundSubscriptionPayment: async (clientId, paymentRecordId, data = {}) => {
        const response = await apiClient.post(
            `/system/subscription-clients/${clientId}/payments/${paymentRecordId}/refund`,
            data
        )
        return response
    },
}
